/**
 * Helpers that turn a document style version + client data into preview HTML fragments.
 */

import { resolveDocumentStyle } from './documentStyleVersions';
import { groupHeaderFieldRows } from './documentSettingsDefaults';

const normalizeHexColor = (value) => String(value || '').trim().toLowerCase().replace(/\s+/g, '');

const sameColor = (a, b) => {
    const na = normalizeHexColor(a);
    const nb = normalizeHexColor(b);
    return na && nb && na === nb;
};

/** Legacy Xtram Business soft-blue header — treat as unset so default white applies. */
const LEGACY_CORPORATE_HEADER_BAND = '#e8f0f8';

export const getPreviewTheme = (columnSettings) => {
    const style = resolveDocumentStyle(columnSettings?.documentStyleVersion);
    const baseTheme = style.theme || {};
    const defaultHeaderBand = style.defaults?.upperPanelColor || '#ffffff';
    let rawHeader = columnSettings?.upperPanelColor;
    // Old Business default was soft blue; migrate to current white default unless user picked another color
    if (
        style.id === 'corporateBlue'
        && normalizeHexColor(rawHeader) === LEGACY_CORPORATE_HEADER_BAND
    ) {
        rawHeader = defaultHeaderBand;
    }
    // Prefer explicit upperPanel / style default — never fall back to accentSoft (soft blue)
    const headerBand = rawHeader || defaultHeaderBand;
    const pageBackground = columnSettings?.backgroundColor
        || style.defaults?.backgroundColor
        || '#ffffff';
    // Document title (כותרת מסמך) — user titleTextColor wins
    const documentTitleColor = columnSettings?.titleTextColor
        || style.defaults?.titleTextColor
        || baseTheme.title
        || baseTheme.accent;
    // Accent chrome: table header, פריטים, סה״כ לתשלום, footer
    const userAccent = columnSettings?.accentColor
        || style.defaults?.accentColor
        || baseTheme.accent
        || '#304FFF';
    const theme = {
        ...baseTheme,
        pageBackground,
        documentTitleColor,
        headerBand,
        accent: userAccent,
        // Remap tokens that matched the original accent so chrome stays consistent
        tableHeaderColor: sameColor(baseTheme.tableHeaderColor, baseTheme.accent)
            || sameColor(baseTheme.tableHeaderColor, baseTheme.title)
            ? userAccent
            : baseTheme.tableHeaderColor,
        tableHeaderBg: sameColor(baseTheme.tableHeaderBg, baseTheme.accent)
            ? userAccent
            : baseTheme.tableHeaderBg,
        divider: sameColor(baseTheme.divider, baseTheme.accent)
            ? userAccent
            : baseTheme.divider,
        totalHighlightBg: sameColor(baseTheme.totalHighlightBg, baseTheme.accent)
            ? userAccent
            : baseTheme.totalHighlightBg,
        totalHighlightColor: sameColor(baseTheme.totalHighlightColor, baseTheme.accent)
            ? userAccent
            : baseTheme.totalHighlightColor,
        dottedDivider: sameColor(baseTheme.dottedDivider, baseTheme.accent)
            ? userAccent
            : baseTheme.dottedDivider,
        // Colored banners (Modern / Bold) still use accent when upperPanel is the band color
        bannerBg: columnSettings?.upperPanelColor || baseTheme.bannerBg || headerBand,
        bandBg: headerBand,
        waveDark: columnSettings?.upperPanelColor || baseTheme.waveDark || headerBand,
        waveLight: baseTheme.waveLight || headerBand,
        // Keep corporate dual top-bars from theme; headerBand colors the company header row only
        topBarDark: baseTheme.topBarDark || userAccent,
        topBarLight: baseTheme.topBarLight || '#A8C5E2',
    };
    return {
        style,
        layout: style.layout,
        tableHeaderMode: style.tableHeaderMode,
        totalsMode: style.totalsMode,
        theme,
    };
};

/** Full-page background layer (watermark / letterhead). */
export const buildPageBackgroundHtml = (
    imageUrl,
    opacity = 0.18,
    {
        offsetX = 0,
        offsetY = 0,
        rotation = 0,
    } = {}
) => {
    if (!imageUrl) return '';
    const safeOpacity = Math.min(0.6, Math.max(0.05, Number(opacity) || 0.18));
    const ox = Math.min(50, Math.max(-50, Number(offsetX) || 0));
    const oy = Math.min(50, Math.max(-50, Number(offsetY) || 0));
    const rot = Math.min(180, Math.max(-180, Number(rotation) || 0));
    const needsOversize = Math.abs(rot) > 0.5 || Math.abs(ox) > 0.5 || Math.abs(oy) > 0.5;
    const size = needsOversize ? 140 : 100;
    return `
        <div class="page-background" style="
            position: absolute;
            inset: 0;
            z-index: 0;
            pointer-events: none;
            overflow: hidden;
        ">
            <img src="${imageUrl}" alt="" style="
                position: absolute;
                left: 50%;
                top: 50%;
                width: ${size}%;
                height: ${size}%;
                max-width: none;
                object-fit: cover;
                object-position: center;
                opacity: ${safeOpacity};
                transform: translate(calc(-50% + ${ox}%), calc(-50% + ${oy}%)) rotate(${rot}deg);
                transform-origin: center center;
            " />
        </div>
    `;
};

export const buildThemeCss = (theme, tableHeaderMode, layout) => {
    const thBg = theme.tableHeaderBg || '#E8EEFF';
    const thColor = theme.tableHeaderColor || '#304FFF';
    const zebra = theme.zebraRow || '#f8f9fa';
    const accent = theme.accent || '#304FFF';

    const zebraAlt = theme.zebraRowAlt || '#ffffff';

    const thFontSize = layout === 'classic' ? 12 : 14;
    let thExtra = '';
    // Shared content inset — meta row, dividers, and items table all use this width
    const contentInset = 18;
    const headerLine = theme.divider || '#222222';

    if (tableHeaderMode === 'accentText') {
        // Green Minimal / Modern Banner: accent header text + underline under header cells only
        thExtra = `
            background: transparent !important;
            color: ${thColor} !important;
            font-weight: 700 !important;
            border-top: none !important;
            border-bottom: 2px solid ${headerLine} !important;
            border-radius: 0 !important;
            padding-top: 8px !important;
            padding-bottom: 8px !important;
            font-size: ${thFontSize}px !important;
        `;
    } else if (tableHeaderMode === 'lineHeader') {
        // Formal black/white: bold headers between thick horizontal rules
        thExtra = `
            background: transparent !important;
            color: #000000 !important;
            font-weight: 700 !important;
            border-top: 2px solid #111111 !important;
            border-bottom: 2px solid #111111 !important;
            border-radius: 0 !important;
            padding-top: 8px !important;
            padding-bottom: 8px !important;
            font-size: ${thFontSize}px !important;
        `;
    } else if (tableHeaderMode === 'tealLine') {
        // Teal modern: full-width accent top rule on table (matches content inset), muted headers
        thExtra = `
            background: transparent !important;
            color: ${thColor} !important;
            font-weight: 600 !important;
            border-top: none !important;
            border-bottom: none !important;
            border-radius: 0 !important;
            padding-top: 10px !important;
            padding-bottom: 8px !important;
            font-size: ${thFontSize}px !important;
        `;
    } else if (tableHeaderMode === 'cleanLine') {
        // Corporate clean: plain column headers + light grey rule under header row
        thExtra = `
            background: transparent !important;
            color: ${thColor} !important;
            font-weight: 600 !important;
            border-top: none !important;
            border-bottom: 1px solid ${theme.rowDivider || '#D0D0D0'} !important;
            border-radius: 0 !important;
            padding-top: 8px !important;
            padding-bottom: 8px !important;
            font-size: ${thFontSize}px !important;
        `;
    } else if (tableHeaderMode === 'navyGrey') {
        // Navy card: navy header labels, grey body rows, white cell separators
        thExtra = `
            background: transparent !important;
            color: ${thColor} !important;
            font-weight: 700 !important;
            border: none !important;
            border-radius: 0 !important;
            padding-top: 8px !important;
            padding-bottom: 10px !important;
            font-size: ${thFontSize}px !important;
        `;
    } else if (tableHeaderMode === 'solidAccent' || tableHeaderMode === 'solidDark') {
        thExtra = `background: ${thBg} !important; color: ${thColor} !important; border-radius: 0 !important; font-size: ${thFontSize}px !important;`;
    } else if (tableHeaderMode === 'softBand') {
        thExtra = `background: ${thBg} !important; color: ${thColor} !important; text-transform: uppercase; letter-spacing: 0.04em; font-size: ${thFontSize}px !important;`;
    } else {
        thExtra = `background: ${thBg} !important; color: ${thColor} !important; font-size: ${thFontSize}px !important;`;
    }

    const headerRightColor = theme.headerRightColor || theme.title || accent;

    // Document titles follow titleTextColor (documentTitleColor); accents stay theme accent
    const titleColor = theme.documentTitleColor || theme.title || accent;
    return `
        .style-title { color: ${titleColor} !important; }
        .style-accent { color: ${accent} !important; }
        .style-header-right { color: ${headerRightColor} !important; }
        .doc-content-inset {
            width: 100%;
            box-sizing: border-box;
            padding-left: ${contentInset}px;
            padding-right: ${contentInset}px;
            align-self: stretch;
        }
        .style-divider {
            width: auto;
            height: 2px;
            background: ${theme.divider || accent};
            margin: 8px ${contentInset}px 12px;
            align-self: stretch;
            box-sizing: border-box;
        }
        .style-divider-thick {
            width: auto;
            height: 3px;
            background: ${theme.divider || accent};
            margin: 6px ${contentInset}px;
            align-self: stretch;
            box-sizing: border-box;
        }
        table.preview-items {
            border-collapse: collapse !important;
            border-spacing: 0 !important;
            width: 100% !important;
        }
        table.preview-items th {
            ${thExtra}
            font-size: ${thFontSize}px !important;
        }
        table.preview-items td {
            border: none !important;
            background-clip: padding-box;
        }
        /* Green-minimal sample: 1st data row gray, 2nd white, then alternate */
        ${tableHeaderMode === 'accentText' ? `
        table.preview-items tbody tr:nth-child(odd) { background-color: ${zebra} !important; }
        table.preview-items tbody tr:nth-child(even) { background-color: ${zebraAlt} !important; }
        table.preview-items tbody tr:hover { background-color: ${zebra} !important; }
        ` : tableHeaderMode === 'lineHeader' ? `
        table.preview-items tbody tr td {
            border-bottom: 1px solid #cccccc !important;
            padding-top: 8px !important;
            padding-bottom: 8px !important;
        }
        table.preview-items tbody tr { background-color: #ffffff !important; }
        ` : tableHeaderMode === 'tealLine' ? `
        table.preview-items {
            border-top: 3px solid ${accent} !important;
            width: 100% !important;
            box-sizing: border-box !important;
        }
        table.preview-items tbody tr td {
            border-bottom: 1px solid ${theme.dottedDivider || accent} !important;
            padding-top: 10px !important;
            padding-bottom: 10px !important;
        }
        table.preview-items tbody tr { background-color: #ffffff !important; }
        ` : tableHeaderMode === 'cleanLine' ? `
        table.preview-items tbody tr td {
            border-bottom: 1px solid ${theme.rowDivider || '#D0D0D0'} !important;
            padding-top: 10px !important;
            padding-bottom: 10px !important;
            background: #ffffff !important;
        }
        table.preview-items tbody tr { background-color: #ffffff !important; }
        table.preview-items thead tr:first-child th:first-child,
        table.preview-items thead tr:first-child th:last-child {
            border-radius: 0 !important;
        }
        ` : tableHeaderMode === 'navyGrey' ? `
        table.preview-items {
            border-collapse: separate !important;
            border-spacing: 3px 3px !important;
            width: 100% !important;
        }
        table.preview-items thead tr:first-child th:first-child,
        table.preview-items thead tr:first-child th:last-child {
            border-radius: 0 !important;
        }
        table.preview-items tbody tr td {
            background: ${zebra} !important;
            border: none !important;
            border-radius: 0 !important;
            padding: 11px 12px !important;
            color: #000000 !important;
            text-align: right !important;
        }
        /* סה״כ column — same black value style, left-aligned amount */
        table.preview-items tbody tr td:last-child {
            color: #000000 !important;
            text-align: left !important;
            white-space: nowrap !important;
        }
        table.preview-items tbody tr { background-color: transparent !important; }
        ` : `
        table.preview-items tbody tr:nth-child(odd) { background-color: ${zebraAlt} !important; }
        table.preview-items tbody tr:nth-child(even) { background-color: ${zebra} !important; }
        table.preview-items tbody tr:hover { background-color: ${zebra} !important; }
        `}
        .total-highlight {
            background: ${theme.totalHighlightBg || accent};
            color: ${theme.totalHighlightColor || '#fff'};
            font-weight: 700;
            padding: 8px 12px;
            border-radius: 6px;
        }
        .meta-band {
            background: ${theme.headerBand || theme.bandBg || '#FFFFFF'};
            padding: 12px 20px;
            margin: 0 0 14px;
            border-top: 2px solid ${theme.topBarDark || accent};
            border-bottom: 2px solid ${theme.topBarDark || accent};
            width: 100%;
            max-width: 100%;
            box-sizing: border-box;
            align-self: stretch;
        }
        .top-split-bar {
            display: flex;
            width: 100%;
            max-width: 100%;
            height: 8px;
            align-self: stretch;
            box-sizing: border-box;
        }
        .top-split-bar .light { flex: 1; background: ${theme.topBarLight || '#A8C5E2'}; }
        .top-split-bar .dark { flex: 1.2; background: ${theme.topBarDark || accent}; }
        .banner-header {
            background: ${theme.headerBand || theme.bannerBg || accent};
            color: ${theme.bannerText || '#fff'};
            padding: 16px 20px;
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            gap: 16px;
            width: 100%;
        }
        .wave-header {
            width: 100%;
            height: 26px;
            background: linear-gradient(120deg, ${theme.waveLight || '#B8D9F0'} 0%, ${theme.headerBand || theme.waveDark || '#7EB8E0'} 100%);
            clip-path: ellipse(120% 100% at 50% 0%);
            margin-bottom: 6px;
        }
        .styled-header-band {
            background: ${theme.headerBand || 'transparent'};
            width: 100%;
            box-sizing: border-box;
            align-self: stretch;
        }
        /* Client / לכבוד / תאריך — follow page backgroundColor on every layout */
        .client-details-band {
            background: ${theme.pageBackground || '#ffffff'} !important;
        }
        /* Keep footer-box / brand bars absolutely positioned at page bottom */
        .a4 > *:not(.page-background):not(.footer-box):not(.footer-brand-bar) { position: relative; z-index: 1; }
    `;
};

export const escapeHtml = (value) => String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

/**
 * Company header model shared by every layout. Saved `headerDetails` win over the live client
 * record; the client is only used for legacy settings that have no header yet.
 * Mirrors DocumentHeaderDetails.Resolve on the server so preview and PDF print the same lines.
 */
export const resolveCompanyHeader = ({ headerDetails, client, idLabel }) => {
    const fields = Array.isArray(headerDetails?.fields) ? headerDetails.fields : [
        { type: 'businessName', label: '', value: client?.name || 'חברת דוגמה' },
        { type: 'taxId', label: idLabel, value: client?.vatClientNumber?.trim() || client?.caseId || '123456789' },
        { type: 'address', label: 'כתובת', value: client?.location || '' },
        { type: 'phone', label: 'טל1', value: client?.phoneNumber || '' },
        { type: 'phone2', label: 'טל2', value: client?.secondaryPhoneNumber || '' },
        { type: 'email', label: '', value: client?.email || '' },
        { type: 'website', label: '', value: client?.website || '' },
    ];
    const isPrinted = (f) => f && f.visible !== false && String(f.value ?? '').trim();
    const nameField = fields.find((f) => f?.type === 'businessName' && isPrinted(f));
    // Fields chained with `joinPrevious` share one line: "ח"פ: x, כתובת: y"
    const lines = groupHeaderFieldRows(fields)
        .map((row) => row.fields.filter(isPrinted))
        .filter((rowFields) => rowFields.length > 0)
        .map((rowFields) => ({
            type: rowFields[0].type,
            parts: rowFields.map((f) => ({ label: String(f.label ?? '').trim(), value: String(f.value).trim() })),
        }));
    return { companyName: nameField ? String(nameField.value).trim() : '', lines };
};

export const buildCompanyInfoLinesHtml = (lines, style = '') => (lines || []).map((line) => {
    const content = line.parts
        .map(({ label, value }) => (label
            ? `<strong>${escapeHtml(label)}</strong>: ${escapeHtml(value)}`
            : escapeHtml(value)))
        .join(', ');
    return `<div style="${style}">${content}</div>`;
}).join('');

/**
 * Build header HTML for non-classic layouts.
 */
export const buildStyledHeaderHtml = ({
    layout,
    theme,
    client,
    logoHtml,
    hasLogo,
    companyInfoTextSize,
    textSize,
    clientDetailsTextSize,
    companyInfoTextColor,
    clientDetailsTextColor,
    fontFamilyCss,
    aling,
    direction,
    headerRightText,
    headerLeftText,
    idLabel,
    title,
    documentTitleTextSize,
    dateAlign,
    titleTextColor,
    boldDate = false,
    tableHeaderMode,
    logoSlot,
    logoPosition = 'left',
    headerDetails = null,
}) => {
    const logoW = logoSlot?.widthPx || 96;
    const logoH = logoSlot?.heightPx || 96;
    const clientSize = clientDetailsTextSize || textSize || 13;
    const infoColor = companyInfoTextColor || titleTextColor || '#000000';
    const clientColor = clientDetailsTextColor || '#000000';
    // Same page color as .a4 — client/לכבוד blocks must not cover it with hardcoded white
    const pageBg = theme.pageBackground || '#ffffff';
    const clientBandStyle = `background: ${pageBg}; box-sizing: border-box;`;
    const logoBox = (extraStyle = '') => (
        hasLogo
            ? `<div style="width: ${logoW}px; height: auto; flex-shrink: 0; ${extraStyle}">${logoHtml}</div>`
            : ''
    );
    const companyHeader = resolveCompanyHeader({ headerDetails, client, idLabel });
    const companyName = escapeHtml(companyHeader.companyName);
    const companyInfoHtml = (style) => buildCompanyInfoLinesHtml(companyHeader.lines, style);
    const dateStr = new Date().toLocaleDateString('he-IL');
    const recipient = client?.clientName || 'לקוח דוגמה';
    const recipientId = client?.clientCaseId || '987654321';
    const recipientPhone = client?.clientPhoneNumber || client?.phoneNumber || '';
    const recipientLocation = client?.clientLocation || '';
    const dateWeight = boldDate ? 700 : 400;
    const recipientPhoneHtml = (style = 'font-size: 14px; color: #444; margin-top: 2px;') => (
        recipientPhone
            ? `<div style="${style}"><strong>מספר טלפון</strong>: ${recipientPhone}</div>`
            : ''
    );
    const headerRightColor = theme.headerRightColor || theme.title || titleTextColor || '#000';

    const companyLines = `
        <div class="haktsaa style-header-right" style="font-size: ${companyInfoTextSize}px; font-weight: 700; text-align: ${aling}; color: ${headerRightColor || infoColor};">${companyName}</div>
        ${headerRightText ? `<div class="style-header-right" style="font-size: ${companyInfoTextSize - 2}px; text-align: ${aling}; margin-bottom: 4px; color: ${headerRightColor || infoColor}; font-weight: 600;">${headerRightText}</div>` : ''}
        ${companyInfoHtml(`font-size: ${Math.max(11, companyInfoTextSize - 3)}px; text-align: ${aling}; color: ${infoColor};`)}
        ${headerLeftText ? `<div style="font-size: ${Math.max(11, companyInfoTextSize - 3)}px; text-align: ${aling}; margin-top: 4px; color: ${infoColor};">${headerLeftText}</div>` : ''}
    `;

    // Shared meta row: in RTL, לכבוד on the visual RIGHT, date on the visual LEFT.
    // Use direction:ltr + explicit text-align so flex order is predictable across styles.
    const recipientDateRow = `
        <div class="doc-content-inset client-details-band" style="
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            margin: 6px 0 10px;
            direction: ltr;
            font-family: ${fontFamilyCss};
            font-size: ${clientSize}px;
            gap: 24px;
            color: ${clientColor};
            ${clientBandStyle}
        ">
            <div style="text-align: left; flex: 0 0 auto; min-width: 130px;">
                <div style="color: ${clientColor}; font-size: 14px; font-weight: 700; margin-bottom: 4px;">מקור</div>
                <div style="color: ${clientColor}; font-size: ${clientSize}px; font-weight: 600; margin-bottom: 4px;">תאריך</div>
                <div style="font-weight: ${dateWeight}; font-size: ${clientSize + 2}px; color: ${clientColor};">${dateStr}</div>
            </div>
            <div style="text-align: right; flex: 1 1 auto; direction: rtl;">
                <div style="color: ${clientColor}; font-size: ${clientSize}px; font-weight: 600; margin-bottom: 4px;">לכבוד</div>
                <div style="font-weight: 700; font-size: ${clientSize + 2}px; color: ${clientColor};">${recipient}</div>
                <div style="font-size: ${clientSize}px; color: ${clientColor}; font-weight: 600; margin-top: 2px; opacity: 0.85;">ח"פ/ע"מ: ${recipientId}</div>
                ${recipientLocation ? `<div style="font-size: ${Math.max(11, clientSize - 1)}px; color: ${clientColor}; margin-top: 2px; opacity: 0.8;">${recipientLocation}</div>` : ''}
                ${recipientPhoneHtml(`font-size: ${Math.max(11, clientSize - 1)}px; color: ${clientColor};`)}
            </div>
        </div>
    `;

    if (layout === 'modernBanner') {
        const infoSize = Math.max(11, companyInfoTextSize - 3);
        // Banner sits on solid accent — default header text is white (ignore generic black)
        const isGenericBlack = (c) => !c || /^#0{3,8}$/i.test(String(c).trim()) || String(c).trim().toLowerCase() === 'black';
        const bannerText = isGenericBlack(companyInfoTextColor)
            ? (theme.bannerText || '#FFFFFF')
            : companyInfoTextColor;
        const bannerTitle = isGenericBlack(titleTextColor)
            ? (theme.bannerText || '#FFFFFF')
            : (titleTextColor || theme.bannerText || '#FFFFFF');
        return `
            <div class="banner-header" style="direction: ${direction}; font-family: ${fontFamilyCss};">
                <div style="font-size: ${documentTitleTextSize + 8}px; font-weight: 800; letter-spacing: 0.04em; color: ${bannerTitle};">${title}</div>
                <div style="text-align: ${aling === 'right' ? 'left' : 'right'}; font-size: ${infoSize}px; line-height: 1.5; opacity: 0.95; color: ${bannerText};">
                    <div style="font-weight: 700; font-size: ${companyInfoTextSize}px; margin-bottom: 4px; color: ${bannerText};">${companyName}</div>
                    ${headerRightText ? `<div style="font-weight: 600; font-size: ${Math.max(11, companyInfoTextSize - 2)}px; margin-bottom: 4px; color: ${bannerText};">${headerRightText}</div>` : ''}
                    ${companyInfoHtml(`font-size: ${infoSize}px; color: ${bannerText};`)}
                    ${headerLeftText ? `<div style="margin-top: 4px; color: ${bannerText};">${headerLeftText}</div>` : ''}
                </div>
            </div>
            <div class="client-details-band" style="
                display: flex;
                justify-content: space-between;
                align-items: flex-start;
                padding: 12px 16px;
                direction: ltr;
                font-family: ${fontFamilyCss};
                width: 100%;
                box-sizing: border-box;
                gap: 28px;
                color: ${clientColor};
                font-size: ${clientSize}px;
                ${clientBandStyle}
            ">
                <div style="text-align: left; flex: 0 0 auto; min-width: 130px;">
                    <div style="color: ${clientColor}; font-size: 14px; font-weight: 700; margin-bottom: 4px;">מקור</div>
                    <div style="color: ${clientColor}; font-size: ${clientSize}px; font-weight: 600; margin-bottom: 4px;">תאריך</div>
                    <div style="font-weight: ${dateWeight}; font-size: ${clientSize + 2}px; color: ${clientColor};">${dateStr}</div>
                </div>
                <div style="text-align: center; flex: 0 0 auto;">
                    <div style="color: ${clientColor}; font-size: ${clientSize}px; font-weight: 600;">סה״כ לתשלום</div>
                    <div class="style-accent" style="font-size: ${clientSize + 10}px; font-weight: 800;">₪ 1,180.00</div>
                </div>
                <div style="text-align: right; flex: 1 1 auto; direction: rtl; min-width: 150px;">
                    <div style="color: ${clientColor}; font-size: ${clientSize}px; font-weight: 600; margin-bottom: 4px;">לכבוד</div>
                    <div style="font-weight: 700; font-size: ${clientSize + 2}px; color: ${clientColor};">${recipient}</div>
                    <div style="font-size: ${clientSize}px; margin-top: 2px; color: ${clientColor}; opacity: 0.85;">ח"פ/ע"מ: ${recipientId}</div>
                    ${recipientLocation ? `<div style="font-size: ${Math.max(11, clientSize - 1)}px; color: ${clientColor}; margin-top: 2px; opacity: 0.8;">${recipientLocation}</div>` : ''}
                    ${recipientPhoneHtml(`font-size: ${Math.max(11, clientSize - 1)}px; color: ${clientColor};`)}
                </div>
            </div>
            <div class="style-divider-thick"></div>
        `;
    }

    if (layout === 'corporateBand') {
        // Full-width: top bars → logo LEFT + company RIGHT → title center → date left / לכבוד right
        const infoSize = Math.max(11, companyInfoTextSize - 3);
        const corporateCompany = `
            <div style="direction: rtl; text-align: right;">
                <div style="font-size: ${companyInfoTextSize}px; font-weight: 700; color: ${headerRightColor || infoColor || theme.title || theme.accent || '#2B5EA8'}; margin-bottom: 4px;">${companyName}</div>
                ${headerRightText ? `<div style="font-size: ${Math.max(12, companyInfoTextSize - 2)}px; color: ${headerRightColor || infoColor || theme.title || theme.accent}; margin-bottom: 4px; font-weight: 600;">${headerRightText}</div>` : ''}
                <div style="font-size: ${infoSize}px; color: ${infoColor}; line-height: 1.45;">
                    ${companyInfoHtml(`font-size: ${infoSize}px; color: ${infoColor};`)}
                    ${headerLeftText ? `<div style="margin-top: 4px;">${headerLeftText}</div>` : ''}
                </div>
            </div>
        `;
        return `
            <div style="width: 100%; align-self: stretch; box-sizing: border-box;">
                <div style="width: 100%; height: 6px; background: ${theme.topBarDark || theme.headerBand || theme.accent || '#1B3A6B'};"></div>
                <div style="width: 100%; height: 5px; background: ${theme.topBarLight || theme.headerBand || '#A8C5E2'};"></div>
            </div>
            <div class="styled-header-band" style="
                display: flex;
                justify-content: space-between;
                align-items: flex-start;
                gap: 16px;
                padding: 8px 2px 8px;
                direction: ltr;
                font-family: ${fontFamilyCss};
                width: 100%;
                box-sizing: border-box;
                align-self: stretch;
            ">
                ${hasLogo ? logoBox() : `<div style="flex-shrink: 0;"></div>`}
                <div style="flex: 1 1 auto; max-width: 70%;">${corporateCompany}</div>
            </div>
            <div class="client-details-band" style="
                width: 100%;
                box-sizing: border-box;
                border-top: 2px solid ${theme.topBarDark || theme.accent || '#1B3A6B'};
                padding: 12px 10px;
                direction: ltr;
                font-family: ${fontFamilyCss};
                align-self: stretch;
                ${clientBandStyle}
            ">
                <div style="
                    display: flex;
                    justify-content: center;
                    align-items: baseline;
                    flex-wrap: wrap;
                    gap: 10px 14px;
                    margin-bottom: 10px;
                    direction: rtl;
                    color: ${titleTextColor || theme.title || theme.accent};
                    font-family: ${fontFamilyCss};
                ">
                    <span style="font-size: ${documentTitleTextSize}px; font-weight: 800;">${title}</span>
                    <span style="font-size: ${Math.max(14, documentTitleTextSize - 4)}px; font-weight: 700;">מס׳ דוגמא-001</span>
                </div>
                <div style="
                    display: flex;
                    justify-content: space-between;
                    align-items: flex-start;
                    gap: 24px;
                    width: 100%;
                    color: ${clientColor};
                    font-size: ${clientSize}px;
                ">
                    <div style="text-align: left; flex: 0 0 auto; min-width: 140px;">
                        <div style="color: ${clientColor}; font-size: 14px; font-weight: 700; margin-bottom: 4px;">מקור</div>
                        <div style="color: ${clientColor}; font-size: ${clientSize}px; font-weight: 600; margin-bottom: 4px;">תאריך</div>
                        <div style="font-weight: ${dateWeight}; font-size: ${clientSize + 2}px; color: ${clientColor};">${dateStr}</div>
                    </div>
                    <div style="text-align: right; flex: 0 1 auto; min-width: 160px; direction: rtl;">
                        <div style="color: ${clientColor}; font-size: ${clientSize}px; font-weight: 600; margin-bottom: 4px;">לכבוד</div>
                        <div style="font-weight: 700; font-size: ${clientSize + 2}px; color: ${clientColor};">${recipient}</div>
                        <div style="margin-top: 4px; font-size: ${clientSize}px; color: ${clientColor}; opacity: 0.85;">ח"פ/ע"מ: ${recipientId}</div>
                        ${recipientLocation ? `<div style="margin-top: 2px; font-size: ${Math.max(11, clientSize - 1)}px; color: ${clientColor}; opacity: 0.8;">${recipientLocation}</div>` : ''}
                        ${recipientPhoneHtml(`margin-top: 2px; font-size: ${Math.max(11, clientSize - 1)}px; color: ${clientColor};`)}
                    </div>
                </div>
            </div>
        `;
    }

    if (layout === 'softWaves') {
        // Airy header: wave → logo + company → title → date left / לכבוד right
        return `
            <div class="wave-header" style="align-self: stretch; width: 100%;"></div>
            <div class="doc-content-inset" style="
                display: flex;
                justify-content: space-between;
                align-items: flex-start;
                padding-top: 10px;
                padding-bottom: 6px;
                direction: ltr;
                font-family: ${fontFamilyCss};
                gap: 16px;
            ">
                ${logoBox()}
                <div style="flex: 1 1 auto; text-align: right; direction: rtl;">${companyLines}</div>
            </div>
            <div class="doc-content-inset" style="padding-top: 8px; padding-bottom: 4px; text-align: center; font-family: ${fontFamilyCss};">
                <div class="style-title" style="font-size: ${documentTitleTextSize + 2}px; font-weight: 800; letter-spacing: 0.04em; color: ${titleTextColor || theme.documentTitleColor || theme.title || theme.accent};">${title}</div>
                <div class="style-accent" style="font-size: 13px; font-weight: 600; margin-top: 2px;">#דוגמא-001</div>
            </div>
            <div class="doc-content-inset client-details-band" style="
                display: flex;
                justify-content: space-between;
                align-items: flex-start;
                gap: 28px;
                padding-top: 10px;
                padding-bottom: 14px;
                direction: ltr;
                font-family: ${fontFamilyCss};
                font-size: ${clientSize}px;
                color: ${clientColor};
                ${clientBandStyle}
            ">
                <div style="text-align: left; flex: 0 0 auto; min-width: 130px;">
                    <div style="color: ${clientColor}; font-size: 14px; font-weight: 700; letter-spacing: 0.04em; margin-bottom: 4px;">מקור</div>
                    <div style="color: ${clientColor}; font-size: ${clientSize}px; font-weight: 700; letter-spacing: 0.04em; margin-bottom: 4px;">תאריך</div>
                    <div style="font-weight: ${dateWeight}; font-size: ${clientSize + 2}px; color: ${clientColor};">${dateStr}</div>
                </div>
                <div style="text-align: right; flex: 0 1 auto; min-width: 160px; direction: rtl;">
                    <div style="color: ${clientColor}; font-size: ${clientSize}px; font-weight: 700; letter-spacing: 0.04em; margin-bottom: 4px;">לכבוד</div>
                    <div style="font-weight: 700; font-size: ${clientSize + 2}px; color: ${clientColor};">${recipient}</div>
                    <div style="font-size: ${clientSize}px; color: ${clientColor}; margin-top: 2px; opacity: 0.85;">ח"פ/ע"מ: ${recipientId}</div>
                    ${recipientLocation ? `<div style="font-size: ${Math.max(11, clientSize - 1)}px; color: ${clientColor}; margin-top: 2px; opacity: 0.8;">${recipientLocation}</div>` : ''}
                    ${recipientPhoneHtml(`font-size: ${Math.max(11, clientSize - 1)}px; color: ${clientColor};`)}
                </div>
            </div>
            <div class="style-divider" style="margin-top: 0; margin-bottom: 10px; background: ${theme.accent || '#7EB8E0'};"></div>
        `;
    }

    // splitLogoLeft — green minimal / green professional
    if (layout === 'splitLogoLeft') {
        const titleBadgeRow = `
            <div class="doc-content-inset" style="
                display: flex;
                justify-content: space-between;
                align-items: baseline;
                padding-top: 4px;
                padding-bottom: 6px;
                direction: ltr;
                font-family: ${fontFamilyCss};
                gap: 16px;
            ">
                <div style="font-size: 14px; color: #888; text-align: left; flex: 0 0 auto;">מקור</div>
                <div class="style-title" style="font-size: ${documentTitleTextSize}px; font-weight: 800; text-align: right; flex: 1 1 auto; direction: rtl; color: ${titleTextColor || theme.documentTitleColor || theme.title || theme.accent};">${title} מספר דוגמא-001</div>
            </div>
        `;

        const companyHeaderRow = `
            <div class="styled-header-band" style="padding-top: 4px; padding-bottom: 4px;">
            <div class="doc-content-inset" style="display: flex; justify-content: space-between; align-items: flex-start; padding-top: 14px; padding-bottom: 6px; direction: ${direction}; font-family: ${fontFamilyCss}; gap: 16px;">
                ${logoBox(`order: ${direction === 'rtl' ? 2 : 0};`)}
                <div style="flex: 1; text-align: ${aling};">${companyLines}</div>
            </div>
            </div>
        `;

        // Green Professional: title + badge sit above the divider (sample invoice layout)
        if (tableHeaderMode === 'solidAccent') {
            return `
                ${companyHeaderRow}
                ${titleBadgeRow}
                <div class="style-divider" style="margin-top: 0; margin-bottom: 10px;"></div>
                ${recipientDateRow}
            `;
        }

        // Green Minimal: divider first, then date/לכבוד, then title below
        return `
            ${companyHeaderRow}
            <div class="style-divider" style="margin-top: 4px; margin-bottom: 10px;"></div>
            ${recipientDateRow}
            ${titleBadgeRow}
        `;
    }

    // Formal black/white: bordered company box → thick title bar → לכבוד / תאריך
    if (layout === 'formalLines') {
        return `
            <div class="doc-content-inset" style="padding-top: 12px; padding-bottom: 10px; font-family: ${fontFamilyCss};">
                <div class="styled-header-band" style="
                    border: 1.5px solid #111;
                    padding: 10px 14px;
                    display: flex;
                    justify-content: space-between;
                    align-items: flex-start;
                    gap: 16px;
                    direction: ltr;
                    box-sizing: border-box;
                ">
                    ${hasLogo ? logoBox() : `<div style="flex-shrink: 0;"></div>`}
                    <div style="text-align: right; flex: 1 1 auto; direction: rtl;">
                        <div style="font-size: ${companyInfoTextSize}px; font-weight: 800; color: ${infoColor}; margin-bottom: 4px;">${companyName}</div>
                        ${headerRightText ? `<div style="font-size: ${Math.max(12, companyInfoTextSize - 2)}px; color: ${infoColor}; font-weight: 600; margin-bottom: 4px;">${headerRightText}</div>` : ''}
                        ${companyInfoHtml(`font-size: ${Math.max(11, companyInfoTextSize - 3)}px; color: ${infoColor};`)}
                        ${headerLeftText ? `<div style="font-size: ${Math.max(11, companyInfoTextSize - 3)}px; color: ${infoColor}; margin-top: 4px;">${headerLeftText}</div>` : ''}
                    </div>
                </div>
                <div style="
                    margin-top: 12px;
                    border-top: 3px solid #111;
                    border-bottom: 3px solid #111;
                    padding: 8px 6px;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    direction: rtl;
                    gap: 12px;
                ">
                    <div style="font-size: ${documentTitleTextSize}px; font-weight: 800; color: ${titleTextColor || '#000'};">${title}</div>
                    <div style="font-size: ${Math.max(12, documentTitleTextSize - 6)}px; font-weight: 700; color: ${titleTextColor || '#000'};">מספר : דוגמא-001</div>
                    <div style="font-size: 14px; font-weight: 600; color: ${clientColor};">מקור</div>
                </div>
                <div class="client-details-band" style="
                    display: flex;
                    justify-content: space-between;
                    align-items: flex-start;
                    margin-top: 14px;
                    direction: ltr;
                    gap: 28px;
                    color: ${clientColor};
                    font-size: ${clientSize}px;
                    ${clientBandStyle}
                    padding: 4px 0;
                ">
                    <div style="text-align: left; flex: 0 0 auto; min-width: 140px;">
                        <div style="font-size: ${clientSize}px; font-weight: 700; color: ${clientColor}; margin-bottom: 4px;">תאריך: <span style="font-weight: ${dateWeight};">${dateStr}</span></div>
                    </div>
                    <div style="text-align: right; flex: 1 1 auto; direction: rtl;">
                        <div style="font-size: ${clientSize}px; font-weight: 700; color: ${clientColor}; margin-bottom: 4px;">לכבוד:</div>
                        <div style="font-size: ${clientSize + 2}px; font-weight: 700; color: ${clientColor};">${recipient}</div>
                        <div style="font-size: ${clientSize}px; color: ${clientColor}; margin-top: 2px; opacity: 0.85;">ח"פ/ע"מ: ${recipientId}</div>
                        ${recipientLocation ? `<div style="font-size: ${Math.max(11, clientSize - 1)}px; color: ${clientColor}; margin-top: 2px; opacity: 0.8;">${recipientLocation}</div>` : ''}
                        ${recipientPhoneHtml(`font-size: ${Math.max(11, clientSize - 1)}px; color: ${clientColor}; margin-top: 2px;`)}
                    </div>
                </div>
            </div>
        `;
    }

    // Navy card (Xtram Bold): full-width navy header (logo + company) with rounded corner + mint bottom border
    if (layout === 'navyCard') {
        const navy = theme.headerBand || theme.bannerBg || theme.accent || '#2d4d76';
        const mint = theme.mintAccent || '#6de1aa';
        // Logo sits inside the navy band on a white plate so colors stay readable
        const logoBlock = hasLogo
            ? `<div style="
                flex: 0 0 auto;
                display: flex;
                align-items: center;
                justify-content: center;
                padding: 10px 12px;
                background: #ffffff;
                border-radius: 12px;
                box-sizing: border-box;
            ">
                <div style="width: ${logoW}px; height: auto; flex-shrink: 0;">${logoHtml}</div>
            </div>`
            : '';
        return `
            <div style="
                width: 100%;
                align-self: stretch;
                box-sizing: border-box;
                background: ${navy};
                color: #ffffff;
                border-radius: 0 0 0 52px;
                border-bottom: 4px solid ${mint};
                padding: 18px 22px 18px 18px;
                direction: ltr;
                font-family: ${fontFamilyCss};
            ">
                <div style="
                    width: 100%;
                    box-sizing: border-box;
                    display: flex;
                    justify-content: space-between;
                    align-items: flex-start;
                    gap: 16px;
                ">
                    ${logoBlock || '<div style="flex-shrink: 0;"></div>'}
                    <div style="
                        flex: 1 1 auto;
                        direction: rtl;
                        text-align: right;
                        box-sizing: border-box;
                        min-width: 0;
                    ">
                        <div style="font-size: ${Math.max(14, companyInfoTextSize)}px; font-weight: 700; margin-bottom: 8px;">${companyName}</div>
                        ${headerRightText ? `<div style="font-size: ${Math.max(12, companyInfoTextSize - 2)}px; font-weight: 600; margin-bottom: 6px; opacity: 0.95;">${headerRightText}</div>` : ''}
                        <div style="font-size: ${Math.max(11, companyInfoTextSize - 3)}px; line-height: 1.5; opacity: 0.96;">
                            ${companyInfoHtml(`font-size: ${Math.max(11, companyInfoTextSize - 3)}px; color: #ffffff;`)}
                            ${headerLeftText ? `<div style="margin-top: 4px;">${headerLeftText}</div>` : ''}
                        </div>
                    </div>
                </div>
            </div>
            <div class="doc-content-inset" style="
                text-align: center;
                padding: 10px 14px 8px;
                box-sizing: border-box;
                font-family: ${fontFamilyCss};
            ">
                <div style="font-size: ${documentTitleTextSize + 4}px; font-weight: 800; color: ${titleTextColor || navy}; letter-spacing: 0.02em; line-height: 1.25;">
                    ${title}
                </div>
                <div style="font-size: ${Math.max(11, documentTitleTextSize - 7)}px; color: #666; margin-top: 4px;">#דוגמא-001</div>
            </div>
            <div class="client-details-band" style="
                width: 100%;
                box-sizing: border-box;
                padding: 10px 14px 10px;
                display: flex;
                justify-content: space-between;
                align-items: flex-start;
                gap: 24px;
                direction: ltr;
                font-family: ${fontFamilyCss};
                color: ${clientColor};
                font-size: ${clientSize}px;
                ${clientBandStyle}
            ">
                <div style="text-align: left; flex: 0 0 auto; min-width: 130px;">
                    <div style="font-size: 14px; font-weight: 700; color: ${navy}; margin-bottom: 4px;">מקור</div>
                    <div style="font-size: ${clientSize}px; color: ${clientColor}; margin-bottom: 2px;">תאריך: <span style="font-weight: ${dateWeight}; color: ${clientColor};">${dateStr}</span></div>
                </div>
                <div style="text-align: right; flex: 1 1 auto; direction: rtl;">
                    <div style="font-size: ${clientSize}px; font-weight: 700; color: ${clientColor}; margin-bottom: 4px;">לכבוד:</div>
                    <div style="font-size: ${clientSize + 2}px; font-weight: 700; color: ${clientColor};">${recipient}</div>
                    <div style="font-size: ${clientSize}px; color: ${clientColor}; margin-top: 2px; opacity: 0.85;">ח"פ/ע"מ: ${recipientId}</div>
                    ${recipientLocation ? `<div style="font-size: ${Math.max(11, clientSize - 1)}px; color: ${clientColor}; margin-top: 2px; opacity: 0.8;">${recipientLocation}</div>` : ''}
                    ${recipientPhoneHtml(`font-size: ${Math.max(11, clientSize - 1)}px; color: ${clientColor}; margin-top: 2px;`)}
                </div>
            </div>
        `;
    }

    // Teal modern: logo only in header (left/center per settings); title+number always separate below
    if (layout === 'tealModern') {
        const lineColor = theme.divider || theme.accent || '#558B7F';
        const logoEl = hasLogo
            ? `<div style="width: ${logoW}px; height: auto; flex-shrink: 0;">${logoHtml}</div>`
            : '';
        const infoSize = Math.max(11, companyInfoTextSize - 3);
        const companyBlock = `
            <div style="flex: 1 1 auto; text-align: right; direction: rtl;">
                <div style="font-size: ${companyInfoTextSize}px; font-weight: 700; color: ${infoColor};">${companyName}</div>
                ${headerRightText ? `<div style="font-size: ${Math.max(11, companyInfoTextSize - 2)}px; color: ${infoColor}; font-weight: 600; margin-top: 2px;">${headerRightText}</div>` : ''}
                ${companyInfoHtml(`font-size: ${infoSize}px; color: ${infoColor}; margin-top: 2px;`)}
                ${headerLeftText ? `<div style="font-size: ${infoSize}px; color: ${infoColor}; margin-top: 4px;">${headerLeftText}</div>` : ''}
            </div>
        `;
        // Logo stays in header only — never bundled with title text
        const headerRow = logoPosition === 'center' && hasLogo
            ? `
                <div class="styled-header-band">
                <div class="doc-content-inset" style="
                    position: relative;
                    display: flex;
                    justify-content: flex-end;
                    align-items: flex-start;
                    min-height: ${Math.max(56, Math.min(logoH, 120))}px;
                    padding-top: 14px;
                    padding-bottom: 6px;
                    font-family: ${fontFamilyCss};
                ">
                    <div style="position: absolute; left: 50%; top: 14px; transform: translateX(-50%);">
                        ${logoEl}
                    </div>
                    ${companyBlock}
                </div>
                </div>
            `
            : `
                <div class="styled-header-band">
                <div class="doc-content-inset" style="
                    display: flex;
                    justify-content: space-between;
                    align-items: flex-start;
                    gap: 16px;
                    padding-top: 14px;
                    padding-bottom: 6px;
                    direction: ltr;
                    font-family: ${fontFamilyCss};
                ">
                    ${logoEl || '<div style="flex-shrink: 0;"></div>'}
                    ${companyBlock}
                </div>
                </div>
            `;
        return `
            ${headerRow}
            <div class="doc-content-inset" style="margin-bottom: 8px; font-family: ${fontFamilyCss};">
                <div style="
                    border-top: 2px solid ${lineColor};
                    border-bottom: 2px solid ${lineColor};
                    padding: 12px 0;
                    box-sizing: border-box;
                    width: 100%;
                ">
                    <div style="text-align: center; margin-bottom: 12px;">
                        <div style="font-size: ${documentTitleTextSize + 4}px; font-weight: 800; color: ${titleTextColor || theme.title || theme.accent}; letter-spacing: 0.02em;">${title}</div>
                        <div style="font-size: ${Math.max(11, documentTitleTextSize - 7)}px; color: #666; margin-top: 4px;">#דוגמא-001</div>
                    </div>
                    <div class="client-details-band" style="
                        display: flex;
                        justify-content: space-between;
                        align-items: flex-start;
                        gap: 24px;
                        direction: ltr;
                        color: ${clientColor};
                        font-size: ${clientSize}px;
                        ${clientBandStyle}
                        padding: 4px 0;
                    ">
                        <div style="text-align: left; flex: 1 1 30%;">
                            <div style="font-size: 14px; font-weight: 700; color: ${clientColor}; margin-bottom: 4px;">מקור</div>
                            <div style="font-size: ${clientSize}px; font-weight: 700; color: ${clientColor}; margin-bottom: 4px; opacity: 0.7;">תאריך</div>
                            <div style="font-size: ${clientSize}px; font-weight: ${dateWeight}; color: ${clientColor};">${dateStr}</div>
                            <div style="font-size: ${Math.max(11, clientSize - 1)}px; font-weight: 700; color: ${clientColor}; margin-top: 6px; opacity: 0.7;">אופן תשלום</div>
                            <div style="font-size: ${clientSize}px; color: ${clientColor}; margin-top: 4px;">תשלום בעת הקבלה</div>
                        </div>
                        <div style="text-align: right; flex: 1 1 35%; direction: rtl;">
                            <div style="font-size: ${Math.max(11, clientSize - 1)}px; font-weight: 700; color: ${clientColor}; margin-bottom: 6px; opacity: 0.7;">לקוח</div>
                            <div style="font-size: ${clientSize + 1}px; font-weight: 700; color: ${clientColor};">${recipient}</div>
                            <div style="font-size: ${Math.max(11, clientSize - 1)}px; color: ${clientColor}; margin-top: 2px; opacity: 0.85;">ח"פ/ע"מ: ${recipientId}</div>
                            ${recipientLocation ? `<div style="font-size: ${Math.max(11, clientSize - 1)}px; color: ${clientColor}; margin-top: 2px; opacity: 0.8;">${recipientLocation}</div>` : ''}
                            ${recipientPhoneHtml(`font-size: ${Math.max(11, clientSize - 1)}px; color: ${clientColor}; margin-top: 2px;`)}
                        </div>
                    </div>
                </div>
            </div>
        `;
    }

    // classic — caller keeps existing header markup
    return null;
};

/**
 * Style-aware totals block (invoice or simple receipt amounts).
 */
export const buildStyledTotalsHtml = ({
    totalsMode,
    theme,
    direction,
    aling,
    totalPrice,
    tax,
    totalPriceWithTax,
    vatRate,
    isInvoiceTable,
    totalsTextSize = 13,
    totalsTextColor = '#000000',
}) => {
    const accent = theme.accent || '#304FFF';
    const soft = theme.accentSoft || '#E8EEFF';
    const highlightBg = theme.totalHighlightBg && theme.totalHighlightBg !== 'transparent'
        ? theme.totalHighlightBg
        : accent;
    const highlightColor = theme.totalHighlightColor || '#ffffff';
    const labelColor = theme.title || accent;
    const tSize = totalsTextSize || 13;
    const tColor = totalsTextColor || '#000000';
    const tSizeHi = tSize + 2;
    const tSizeVal = tSize + 3;

    const rowsInvoice = [
        { label: 'סה״כ ללא מע״מ', value: totalPrice },
        { label: `${(vatRate * 100)}% מע״מ`, value: tax },
        { label: 'סה״כ לתשלום', value: totalPriceWithTax, highlight: true },
    ];
    const rowsSimple = [
        { label: 'סה״כ ברוטו', value: totalPrice },
        { label: 'ניכוי מס במקור', value: 0 },
        { label: 'סה״כ שולם נטו', value: totalPrice, highlight: true },
    ];
    const rows = isInvoiceTable ? rowsInvoice : rowsSimple;

    // Formal black/white: bordered subtotal/VAT group + boxed total due
    if (totalsMode === 'boxedTotal') {
        const plainRows = rows.filter((r) => !r.highlight);
        const highlightRow = rows.find((r) => r.highlight);
        const plainRowsHtml = plainRows.map((r) => `
            <div style="
                display: flex;
                justify-content: space-between;
                align-items: center;
                gap: 20px;
                padding: 6px 10px;
            ">
                <div style="font-size: ${tSize}px; color: ${tColor}; white-space: nowrap;">${r.label}</div>
                <div style="font-size: ${tSize}px; color: ${tColor}; white-space: nowrap;">₪ ${Number(r.value).toFixed(2)}</div>
            </div>
        `).join('');
        const highlightHtml = highlightRow ? `
            <div style="
                display: flex;
                justify-content: space-between;
                align-items: center;
                gap: 20px;
                padding: 8px 10px;
                margin-top: 6px;
                border: 2px solid #111;
                font-weight: 800;
                background: #fff;
                box-sizing: border-box;
            ">
                <div style="font-size: ${tSizeHi}px; color: ${tColor}; white-space: nowrap;">${highlightRow.label}</div>
                <div style="font-size: ${tSizeVal}px; color: ${tColor}; white-space: nowrap;">₪ ${Number(highlightRow.value).toFixed(2)}</div>
            </div>
        ` : '';
        return `
            <div style="width: 100%; display: flex; justify-content: flex-end; margin: 16px 0 8px; direction: ${direction};">
                <div style="width: 300px; max-width: 100%; box-sizing: border-box; flex-shrink: 0; direction: ${direction};">
                    <div style="
                        border: 1px solid #111;
                        box-sizing: border-box;
                    ">
                        ${plainRowsHtml}
                    </div>
                    ${highlightHtml}
                </div>
            </div>
        `;
    }

    // Teal modern: muted rows + teal label + filled amount pill for total due
    if (totalsMode === 'tealDueBlock') {
        const dueColor = accent || theme.title;
        return `
            <div style="width: 100%; display: flex; justify-content: flex-end; margin: 18px 0 8px; direction: ${direction};">
                <div style="
                    min-width: 280px;
                    max-width: 55%;
                    direction: ${direction};
                    box-sizing: border-box;
                ">
                    ${rows.map((r) => {
                        if (r.highlight) {
                            return `
                                <div style="
                                    display: flex;
                                    justify-content: space-between;
                                    align-items: center;
                                    gap: 16px;
                                    margin-top: 8px;
                                    padding-top: 6px;
                                ">
                                    <div style="font-size: ${tSizeHi}px; font-weight: 700; color: ${dueColor};">${r.label}</div>
                                    <div style="
                                        background: ${dueColor};
                                        color: #fff;
                                        font-size: ${tSizeVal}px;
                                        font-weight: 800;
                                        padding: 8px 14px;
                                        min-width: 110px;
                                        text-align: center;
                                    ">₪ ${Number(r.value).toFixed(2)}</div>
                                </div>
                            `;
                        }
                        return `
                            <div style="
                                display: flex;
                                justify-content: space-between;
                                align-items: center;
                                gap: 20px;
                                padding: 6px 0;
                                border-bottom: 1px solid ${dueColor};
                            ">
                                <div style="font-size: ${tSize}px; color: ${tColor};">${r.label}</div>
                                <div style="font-size: ${tSize}px; color: ${tColor};">₪ ${Number(r.value).toFixed(2)}</div>
                            </div>
                        `;
                    }).join('')}
                </div>
            </div>
        `;
    }

    // greenMinimal / modernBanner / softWaves — stacked lines; total due gets accent border + color
    if (totalsMode === 'stackRight') {
        const zebra = theme.zebraRow || soft || '#f2f2f2';
        const dueColor = accent || theme.title || '#000';
        return `
            <div style="width: 100%; display: flex; justify-content: flex-end; margin: 18px 0 8px; direction: ${direction};">
                <div style="min-width: 260px; max-width: 55%; direction: ${direction}; overflow: hidden; border-radius: 2px;">
                    ${rows.map((r, idx) => {
                        const textColor = r.highlight ? dueColor : tColor;
                        return `
                        <div style="
                            display: flex;
                            justify-content: space-between;
                            align-items: center;
                            gap: 24px;
                            padding: ${r.highlight ? '10px 12px 8px' : '8px 12px'};
                            background: ${idx % 2 === 0 ? zebra : '#ffffff'};
                            ${r.highlight ? `border-top: 2px solid ${dueColor}; font-weight: 700;` : ''}
                        ">
                            <div style="font-size: ${r.highlight ? tSizeHi : tSize}px; font-weight: ${r.highlight ? 700 : 500}; color: ${textColor};">${r.label}</div>
                            <div style="font-size: ${r.highlight ? tSizeVal : tSize}px; font-weight: ${r.highlight ? 700 : 500}; color: ${textColor};">₪ ${Number(r.value).toFixed(2)}</div>
                        </div>
                    `;
                    }).join('')}
                </div>
            </div>
        `;
    }

    // Navy card totals are rendered inside the items table (see buildNavyTotalsRowsHtml)
    // so column widths match סה״כ / הנחה / סכום הנחה exactly.
    if (totalsMode === 'splitNavyDue') {
        return '';
    }

    // Corporate clean: plain rows + only the due amount in a solid accent pill (label outside)
    if (totalsMode === 'amountPill' || totalsMode === 'totalDueBar') {
        const pillBg = highlightBg;
        const pillColor = highlightColor;
        const rowBorder = theme.divider || accent || '#1B3A6B';
        return `
            <div style="width: 100%; display: flex; justify-content: flex-end; margin: 16px 0 8px; direction: ${direction};">
                <div style="min-width: 260px; max-width: 50%; direction: ${direction};">
                    ${rows.map((r) => {
                        if (r.highlight) {
                            return `
                                <div style="
                                    display: flex;
                                    justify-content: space-between;
                                    align-items: center;
                                    gap: 16px;
                                    margin-top: 8px;
                                    padding-top: 4px;
                                ">
                                    <div style="font-size: ${tSizeHi}px; font-weight: 600; color: ${tColor};">${r.label}</div>
                                    <div style="
                                        background: ${pillBg};
                                        color: ${pillColor};
                                        font-size: ${tSizeVal}px;
                                        font-weight: 800;
                                        padding: 8px 16px;
                                        min-width: 120px;
                                        text-align: center;
                                    ">₪ ${Number(r.value).toFixed(2)}</div>
                                </div>
                            `;
                        }
                        return `
                            <div style="
                                display: flex;
                                justify-content: space-between;
                                align-items: center;
                                gap: 20px;
                                padding: 8px 0;
                                border-bottom: 1px solid ${rowBorder};
                            ">
                                <div style="font-size: ${tSize}px; color: ${tColor};">${r.label}</div>
                                <div style="font-size: ${tSize}px; color: ${tColor};">₪ ${Number(r.value).toFixed(2)}</div>
                            </div>
                        `;
                    }).join('')}
                </div>
            </div>
        `;
    }

    // greenProfessional — bordered box, last row filled with accent
    if (totalsMode === 'highlightBox') {
        return `
            <div style="width: 100%; display: flex; justify-content: flex-end; margin: 18px 0 8px; direction: ${direction};">
                <div style="
                    width: 52%;
                    max-width: 340px;
                    background: #fff;
                    border: 2px solid ${accent};
                    border-radius: 10px;
                    overflow: hidden;
                    direction: ${direction};
                ">
                    <table style="width: 100%; border-collapse: collapse; direction: ${direction};">
                        <tbody>
                            ${rows.map((r) => `
                                <tr style="${r.highlight ? '' : `border-bottom: 1px solid ${soft};`}">
                                    <td style="
                                        padding: ${r.highlight ? '12px 15px' : '10px 15px'};
                                        text-align: ${aling};
                                        font-size: ${r.highlight ? tSizeHi : tSize}px;
                                        font-weight: ${r.highlight ? 700 : 600};
                                        color: ${r.highlight ? highlightColor : labelColor};
                                        background-color: ${r.highlight ? highlightBg : soft};
                                        width: 55%;
                                    ">${r.label}</td>
                                    <td style="
                                        padding: ${r.highlight ? '12px 15px' : '10px 15px'};
                                        text-align: center;
                                        font-size: ${r.highlight ? tSizeVal : tSizeHi}px;
                                        font-weight: ${r.highlight ? 700 : 500};
                                        color: ${r.highlight ? highlightColor : tColor};
                                        background-color: ${r.highlight ? highlightBg : '#fff'};
                                        width: 45%;
                                    ">₪ ${Number(r.value).toFixed(2)}</td>
                                </tr>
                            `).join('')}
                        </tbody>
                    </table>
                </div>
            </div>
        `;
    }

    // classic — horizontal decorative bar using theme accent
    const pay = isInvoiceTable ? totalPriceWithTax : totalPrice;
    const mid = isInvoiceTable ? tax : 0;
    const left = totalPrice;
    const midLabel = isInvoiceTable ? `${(vatRate * 100)}% מע״מ` : 'ניכוי מס במקור';
    const leftLabel = isInvoiceTable ? 'סה״כ ללא מע״מ' : 'סה״כ ברוטו';
    const payLabel = isInvoiceTable ? 'סה״כ לתשלום' : 'סה״כ שולם נטו';

    return `
        <div class="inner-section" style="
            margin: 16px 0 12px;
            height: 52px;
            position: relative;
            background: ${soft};
            border-bottom: 8px solid ${highlightBg};
            border-bottom-left-radius: 12px;
            border-bottom-right-radius: 12px;
            width: 92%;
            box-sizing: border-box;
        ">
            <div style="display: flex; justify-content: space-between; align-items: center; height: 100%; padding: 0 20px; direction: ${direction};">
                <div style="text-align: center; width: 33.33%;">
                    <div style="font-size: ${tSize}px; font-weight: 600; color: ${labelColor};">${payLabel}</div>
                    <div style="font-size: ${tSizeVal}px; font-weight: 600; color: ${tColor};">₪ ${Number(pay).toFixed(2)}</div>
                </div>
                <div style="text-align: center; width: 33.33%;">
                    <div style="font-size: ${tSize}px; font-weight: 600; color: ${labelColor};">${midLabel}</div>
                    <div style="font-size: ${tSizeVal}px; font-weight: 500; color: ${tColor};">₪ ${Number(mid).toFixed(2)}</div>
                </div>
                <div style="text-align: center; width: 33.33%;">
                    <div style="font-size: ${tSize}px; font-weight: 600; color: ${labelColor};">${leftLabel}</div>
                    <div style="font-size: ${tSizeVal}px; font-weight: 500; color: ${tColor};">₪ ${Number(left).toFixed(2)}</div>
                </div>
            </div>
        </div>
    `;
};

/**
 * Items section title + optional notes/signature row, styled per template.
 */
export const buildStyledDetailsAndSignatureHtml = ({
    layout,
    theme,
    direction,
    customDescription,
    signaturePreview,
    signatureSlot,
    titleTextColor,
}) => {
    const accent = theme.accent || '#304FFF';
    const titleColor = titleTextColor || theme.documentTitleColor || theme.title || accent;
    const desc = customDescription
        ? customDescription.replace(/\n/g, '<br>')
        : 'תיאור מותאם לדוגמה';
    const sigImg = signaturePreview
        ? `<div class="signature-slot" style="width:${signatureSlot.widthPx}px;height:${signatureSlot.heightPx}px;"><img src="${signaturePreview}" alt="Signature" /></div>`
        : '';
    const signaturePlaceholder = '<div style="width: 120px; height: 48px; border-bottom: 1px solid #ccc;"></div>';
    const signatureColumn = ({ minWidth = 150, labelGap = 4, fallback = signaturePlaceholder } = {}) => `
                <div style="flex: 0 0 auto; display: flex; flex-direction: column; align-items: flex-start; min-width: ${minWidth}px; direction: rtl;">
                    <div style="font-size: 14px; font-weight: 700; color: #333; margin-bottom: ${labelGap}px; text-align: center;">חתימה:</div>
                    ${sigImg || fallback}
                </div>`;

    // Green-style: notes on left, "חתימה:" + image on right (classic unchanged)
    if (layout === 'splitLogoLeft') {
        return `
            <div style="
                display: flex;
                justify-content: space-between;
                align-items: flex-start;
                width: 100%;
                box-sizing: border-box;
                padding: 12px 18px 8px;
                direction: rtl;
                gap: 24px;
            ">
                <div style="flex: 1 1 auto; text-align: right; direction: rtl;">
                    <div style="font-size: 16px; font-weight: 700; color: ${accent}; margin-bottom: 2px; border-bottom: 2px solid ${accent}; display: inline-block; padding-bottom: 2px;">פרטים</div>
                    <div style="font-size: 14px; color: #222; line-height: 1.5; margin-top: 2px;">${desc}</div>
                </div>
                ${signatureColumn({ minWidth: 140, labelGap: 2, fallback: '' })}
            </div>
        `;
    }

    // Formal lines: same signature pattern as other styles (חתימה on the right, no שם המקבל / תאריך lines)
    if (layout === 'formalLines') {
        return `
            <div class="doc-content-inset" style="
                display: flex;
                justify-content: space-between;
                align-items: flex-start;
                padding-top: 18px;
                padding-bottom: 8px;
                direction: rtl;
                gap: 28px;
            ">
                <div style="flex: 1 1 auto; text-align: right; direction: rtl;">
                    <div style="font-size: 13px; font-weight: 700; color: ${accent}; margin-bottom: 4px;">פרטים</div>
                    <div style="font-size: 13px; color: #222; line-height: 1.5;">${desc}</div>
                </div>
                ${signatureColumn({ labelGap: 6 })}
            </div>
        `;
    }

    // Teal modern: thank-you + signature on right
    // Teal modern: same signature / פרטים pattern as formalLines
    if (layout === 'tealModern') {
        return `
            <div class="doc-content-inset" style="
                display: flex;
                justify-content: space-between;
                align-items: flex-start;
                padding-top: 18px;
                padding-bottom: 8px;
                direction: rtl;
                gap: 28px;
            ">
                <div style="flex: 1 1 auto; text-align: right; direction: rtl;">
                    <div style="font-size: 13px; font-weight: 700; color: ${accent}; margin-bottom: 4px;">פרטים</div>
                    <div style="font-size: 13px; color: #222; line-height: 1.5;">${desc}</div>
                </div>
                ${signatureColumn({ labelGap: 6 })}
            </div>
        `;
    }

    // Navy card: notes + signature, full-bleed padding
    if (layout === 'navyCard') {
        return `
            <div style="
                display: flex;
                justify-content: space-between;
                align-items: flex-start;
                width: 100%;
                box-sizing: border-box;
                padding: 16px 14px 8px;
                direction: rtl;
                gap: 28px;
            ">
                <div style="flex: 1 1 auto; text-align: right; direction: rtl;">
                    <div style="font-size: 14px; font-weight: 700; color: ${accent}; margin-bottom: 4px;">פרטים</div>
                    <div style="font-size: 14px; color: #333; line-height: 1.5;">${desc}</div>
                </div>
                ${signatureColumn()}
            </div>
        `;
    }

    // Corporate / modern / soft waves: notes on left, signature on right
    if (layout === 'corporateBand' || layout === 'modernBanner' || layout === 'softWaves') {
        const notesLabel = layout === 'softWaves' ? 'פרטים' : 'הערות על המסמך / פרטים';
        return `
            <div style="
                display: flex;
                justify-content: space-between;
                align-items: flex-start;
                width: 100%;
                box-sizing: border-box;
                padding: 20px 20px 10px;
                direction: rtl;
                gap: 28px;
            ">
                <div style="flex: 1 1 auto; text-align: right; direction: rtl;">
                    <div style="font-size: 15px; font-weight: 700; color: ${accent}; margin-bottom: 2px;">${notesLabel}</div>
                    <div style="font-size: 14px; color: #333; line-height: 1.5;">${desc}</div>
                </div>
                ${signatureColumn({ labelGap: 2 })}
            </div>
        `;
    }

    // classic
    return `
        <div class="signature-container" style="direction: ${direction};">
            <div class="description-box">
                <div style="font-size: 15px; font-weight: 700; color: ${titleColor}; margin-bottom: 6px;">פרטים</div>
                <div style="font-size: 14px; font-weight: 400; color: #000; line-height: 1.5; direction: rtl;">${desc}</div>
            </div>
            <div class="signature-box">${sigImg}</div>
        </div>
    `;
};

/**
 * Custom footer text + bottom brand bar, colored per style.
 */
export const buildStyledFooterHtml = ({
    layout,
    theme,
    customFooter,
    fontFamilyCss,
}) => {
    const accent = theme.accent || '#304FFF';
    const soft = theme.accentSoft || '#E8EEFF';
    const footerText = customFooter
        ? customFooter.replace(/\n/g, '<br>')
        : 'כותרת תחתונה מותאמת לדוגמה';

    // Old classic position: centered above the bottom brand bar
    const customBlock = `
        <div class="footer-box">
            <div style="height: auto; display: flex; justify-content: center; align-items: center; direction: rtl; font-size: 12px; color: #444; line-height: 1.45; text-align: center;">
                ${footerText}
            </div>
        </div>
    `;

    const brandBarBase = `
        width: 100%;
        align-self: stretch;
        box-sizing: border-box;
        position: absolute;
        left: 0;
        right: 0;
        bottom: 0;
        z-index: 2;
    `;

    if (layout === 'corporateBand') {
        // Green Professional structure + Corporate Blue colors; custom footer bottom-center
        const barColor = theme.topBarDark || accent;
        return `
            ${customBlock}
            <div class="footer-brand-bar" style="${brandBarBase} border-top: 2px solid ${barColor}; padding: 8px 16px 6px; background: #ffffff;">
                <div style="display: flex; justify-content: space-between; align-items: center; direction: ltr; font-size: 10px; color: #333; font-family: ${fontFamilyCss};">
                    <div>www.xtram.co.il · טל: 04-6590004</div>
                    <div style="font-weight: 600; color: ${barColor};">Xtram מסמך זה הופק במערכת</div>
                </div>
            </div>
        `;
    }

    if (layout === 'navyCard') {
        const mint = theme.mintAccent || '#6de1aa';
        return `
            ${customBlock}
            <div class="footer-brand-bar" style="${brandBarBase} background: ${accent}; padding: 10px 16px; color: #fff;">
                <div style="display: flex; justify-content: space-between; align-items: center; direction: ltr; font-size: 10px; font-family: ${fontFamilyCss};">
                    <div>www.xtram.co.il</div>
                    <div style="text-align: right; direction: rtl;">
                        <div style="width: 72px; height: 3px; background: ${mint}; margin: 0 0 5px auto; border-radius: 1px;"></div>
                        <div style="font-weight: 600;">Xtram מסמך זה הופק במערכת</div>
                    </div>
                </div>
            </div>
        `;
    }

    if (layout === 'modernBanner') {
        return `
            ${customBlock}
            <div class="footer-brand-bar" style="${brandBarBase} background: ${accent}; padding: 8px 12px; text-align: center;">
                <span style="font-family: ${fontFamilyCss}; font-size: 10px; font-weight: 600; color: #fff;">
                   Xtram מסמך זה הופק במערכת | טל: 04-6590004 | www.xtram.co.il
                </span>
            </div>
        `;
    }

    if (layout === 'softWaves') {
        return `
            ${customBlock}
            <div class="footer-brand-bar" style="${brandBarBase} padding: 0px 12px 2px; text-align: center; background: #ffffff;">
                <div style="height: 2px; background: ${accent}; position: relative; margin-bottom: 8px;">
                    <div style="
                        position: absolute; left: 50%; top: 50%; transform: translate(-50%, -50%);
                        width: 22px; height: 22px; border-radius: 50%; border: 2px solid ${accent};
                        background: #fff; font-size: 10px; line-height: 18px; color: ${accent}; font-weight: 700;
                    ">1</div>
                </div>
                <div style="font-size: 10px; color: #888;">Xtram מסמך זה הופק במערכת · www.xtram.co.il</div>
            </div>
        `;
    }

    if (layout === 'splitLogoLeft') {
        const barColor = accent;
        return `
            ${customBlock}
            <div class="footer-brand-bar" style="${brandBarBase} border-top: 2px solid ${barColor}; padding: 8px 16px 6px; background: #ffffff;">
                <div style="display: flex; justify-content: space-between; align-items: center; direction: ltr; font-size: 10px; color: #333;">
                    <div>www.xtram.co.il · טל: 04-6590004</div>
                    <div style="font-weight: 600; color: ${barColor};">Xtram מסמך זה הופק במערכת </div>
                </div>
            </div>
        `;
    }

    if (layout === 'formalLines') {
        return `
            ${customBlock}
            <div class="footer-brand-bar" style="${brandBarBase} border-top: 1.5px solid #111; padding: 6px 16px 8px; background: #ffffff; text-align: center;">
                <div style="font-family: ${fontFamilyCss}; font-size: 10px; color: #333;">
                    מסמך זה הופק במערכת Xtram | טל: 04-6590004 | www.xtram.co.il
                </div>
            </div>
        `;
    }

    if (layout === 'tealModern') {
        return `
            ${customBlock}
            <div class="footer-brand-bar" style="${brandBarBase} border-top: 3px solid ${accent}; padding: 8px 16px 10px; background: #ffffff;">
                <div style="display: flex; justify-content: space-between; align-items: center; direction: ltr; font-size: 10px; color: #666; font-family: ${fontFamilyCss};">
                    <div>www.xtram.co.il</div>
                    <div style="font-weight: 600; color: ${accent};">Xtram מסמך זה הופק במערכת</div>
                </div>
            </div>
        `;
    }

    // classic Xtram blue bar — custom footer stays bottom-center via .footer-box
    return `
        ${customBlock}
        <div class="footer-brand-bar" style="${brandBarBase} height: 22px; text-align: center; background: ${accent}; -webkit-print-color-adjust: exact; display: flex; flex-direction: row; align-items: center; justify-content: center; padding: 5px;">
            <p style="margin: 0; font-family: ${fontFamilyCss}; font-size: 10px; font-weight: bold; line-height: 1; color: white;">
                <span style="font-weight: bold;">מסמך זה הופק במערכת</span>
                <span style="font-weight: bold;">Xtram</span> |
                טל: 04-6590004 |
                <a href="https://www.xtram.co.il" style="color: white; text-decoration: none; font-weight: bold;">www.xtram.co.il</a>
            </p>
        </div>
    `;
};

/** Items section heading styled per template. */
/**
 * Navy card totals as rows inside the items table so widths match columns:
 * label spans הנחה + סכום הנחה, amount aligns with סה״כ.
 */
export const buildNavyTotalsRowsHtml = ({
    theme,
    totalPrice,
    tax,
    totalPriceWithTax,
    vatRate,
    itemsDisplayMode,
    totalsTextSize = 13,
    totalsTextColor = '#000000',
}) => {
    const navy = theme?.accent || '#2d4d76';
    const soft = theme?.accentSoft || '#EDEDED';
    const tSize = totalsTextSize || 13;
    const tColor = totalsTextColor || '#000000';
    const showQtyDiscount = itemsDisplayMode !== 'descriptionOnly';
    // Full: מק״ט, תיאור, כמות, מחיר, הנחה, סכום הנחה, סה״כ (7)
    // Simple: מק״ט, תיאור, מחיר, סכום הנחה, סה״כ (5)
    const leadingEmptyCols = showQtyDiscount ? 4 : 3; // through מחיר
    const labelColspan = showQtyDiscount ? 2 : 1; // הנחה+סכום הנחה, or סכום הנחה only
    const rows = [
        { label: 'סה״כ ללא מע״מ', value: totalPrice, highlight: false },
        { label: `${(vatRate * 100)}% מע״מ`, value: tax, highlight: false },
        { label: 'סה״כ לתשלום', value: totalPriceWithTax, highlight: true },
    ];
    return rows.map((r) => {
        const bg = r.highlight ? navy : soft;
        const labelColor = r.highlight ? '#ffffff' : navy;
        const valueColor = r.highlight ? '#ffffff' : tColor;
        return `
            <tr class="navy-totals-row">
                <td colspan="${leadingEmptyCols}" style="background: transparent !important; border: none !important; padding: 0 !important;"></td>
                <td colspan="${labelColspan}" style="
                    background: ${bg} !important;
                    color: ${labelColor} !important;
                    font-size: ${r.highlight ? tSize + 1 : tSize}px !important;
                    font-weight: 700 !important;
                    text-align: right !important;
                    padding: 10px 12px !important;
                    white-space: nowrap !important;
                ">${r.label}</td>
                <td style="
                    background: ${bg} !important;
                    color: ${valueColor} !important;
                    font-size: ${r.highlight ? tSize + 2 : tSize}px !important;
                    text-align: left !important;
                    padding: 10px 12px !important;
                    white-space: nowrap !important;
                ">₪ ${Number(r.value).toFixed(2)}</td>
            </tr>
        `;
    }).join('');
};

/** Payment details band used by navyCard preview (matches sample invoice). */
export const buildNavyPaymentSectionHtml = ({ theme, direction, totalPaid }) => {
    const navy = theme?.accent || '#2d4d76';
    const grey = theme?.accentSoft || '#EDEDED';
    const paid = Number(totalPaid || 0).toFixed(2);
    // Amount column width matches the ₪ value cell; label spans סוג תשלום + פרטים + תאריך
    const amountColW = 120;
    const thStyle = `color: ${navy}; font-size: 14px; font-weight: 700; text-align: right; padding: 8px 10px; background: transparent;`;
    const tdStyle = `background: ${grey}; padding: 11px 10px; text-align: right; font-size: 13px; color: #000000;`;
    return `
        <div style="width: 100%; box-sizing: border-box; margin-top: 20px; align-self: stretch;">
            <div style="
                width: 100%;
                background: ${navy};
                color: #ffffff;
                font-size: 14px;
                font-weight: 700;
                text-align: right;
                direction: rtl;
                padding: 10px 14px;
                box-sizing: border-box;
            ">פרטי תשלומים</div>
            <table style="
                width: 100%;
                table-layout: fixed;
                border-collapse: separate;
                border-spacing: 3px 3px;
                direction: rtl;
                margin-top: 2px;
            ">
                <colgroup>
                    <col style="width: auto;" />
                    <col style="width: auto;" />
                    <col style="width: auto;" />
                    <col style="width: ${amountColW}px;" />
                </colgroup>
                <thead>
                    <tr>
                        <th style="${thStyle}">סוג תשלום</th>
                        <th style="${thStyle}">פרטים</th>
                        <th style="${thStyle}">תאריך</th>
                        <th style="${thStyle} width: ${amountColW}px; text-align: left;">סה״כ (₪)</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td style="${tdStyle}">מזומן</td>
                        <td style="${tdStyle}"></td>
                        <td style="${tdStyle}">${new Date().toLocaleDateString('he-IL')}</td>
                        <td style="${tdStyle} width: ${amountColW}px; text-align: left; white-space: nowrap;">₪ ${paid}</td>
                    </tr>
                    <tr>
                        <td style="background: transparent; padding: 0;"></td>
                        <td colspan="2" style="
                            background: ${navy};
                            color: #ffffff;
                            font-size: 14px;
                            font-weight: 700;
                            padding: 11px 14px;
                            text-align: right;
                            border-radius: 0 0 8px 0;
                        ">סה״כ שולם</td>
                        <td style="
                            background: ${navy};
                            color: #ffffff;
                            font-size: 14px;
                            font-weight: 800;
                            padding: 11px 12px;
                            text-align: left;
                            white-space: nowrap;
                            width: ${amountColW}px;
                            box-sizing: border-box;
                            border-radius: 0 0 0 8px;
                        ">₪ ${paid}</td>
                    </tr>
                </tbody>
            </table>
        </div>
    `;
};

export const buildItemsSectionTitleHtml = ({ theme, titleTextColor, layout }) => {
    if (theme?.hideItemsTitle || layout === 'formalLines' || layout === 'tealModern' || layout === 'navyCard') {
        return '';
    }
    const accent = theme.accent || '#1B3A6B';
    // Corporate: full-width solid accent bar with white title (like sample invoice)
    if (layout === 'corporateBand' || theme?.itemsTitleBar) {
        return `
            <div style="
                width: 100%;
                box-sizing: border-box;
                background: ${accent};
                color: #ffffff;
                font-size: 15px;
                font-weight: 700;
                text-align: right;
                direction: rtl;
                padding: 8px 14px;
                margin: 0 0 10px;
            ">פירוט שירותים</div>
        `;
    }
    // פריטים follows accent chrome color (table header / footer / totals highlight)
    const color = accent || titleTextColor || theme.documentTitleColor || theme.title || '#000';
    return `
        <div class="title-inner-payment" style="
            text-align: right;
            color: ${color};
            border-bottom: 2px solid ${accent || color};
            display: inline-block;
            padding-bottom: 2px;
            margin-bottom: 7px;
            width: auto;
            max-width: 100%;
        ">פריטים</div>
    `;
};

